import { Fragment, useState, useCallback, useEffect, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import {
  Heading,
  Button,
  Input,
  Alert,
  Tabs,
  getTabIds,
  SectionLabel,
  MetricLedgerBlock,
  Sheet,
  Dialog,
  EmptyState,
  Skeleton,
  SkeletonText,
  VisuallyHidden,
} from '@aaimplatform/ui';
import { propertyProfileApi } from '../../api/services/property-profile';
import type { PropertyProfileResponse } from '../../api/services/property-profile';
import { propertyValuationsApi } from '../../api/services/property-valuations';
import type { components as PlatformAdminComponents } from '@aaimplatform/institutional-types/generated/platform-admin';
import type { components as PropertyTitleAdminComponents } from '@aaimplatform/institutional-types/property-title-admin';
import {
  ValueRule,
  InlineSparkline,
  ValueHistoryChart,
  usdCompact,
  fmtDateUS,
  fmtTime,
  yearFraction,
} from './PropertyDossierParts';
import { Absent } from './CollateralListParts';
import { useAuthStore } from '../../stores/auth';
import { hasPlatformAdminRole } from '../../lib/platform-admin-roles';

type Profile = PlatformAdminComponents['schemas']['PropertyProfileResponse']['profile'];
type Exemptions = NonNullable<NonNullable<Profile['ownership']>['exemptions']>;
type ValuationRequest = PropertyTitleAdminComponents['schemas']['PropertyValuationRequest'];
type Valuation = PropertyTitleAdminComponents['schemas']['PropertyValuation'];

// A measured 0 renders as $0; an absent value renders as '--'.
function fmtCurrency(val: number | null | undefined): string {
  if (val == null || isNaN(val)) return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(val);
}

// Percent points as the profile carries them (6.5 means 6.5 percent).
function fmtPts(val: number | null | undefined): string {
  if (val == null || isNaN(val)) return '--';
  return `${val.toFixed(1)}%`;
}

function fmtNum(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 3 });
}

function str(v: unknown): string | undefined {
  return v == null || v === '' ? undefined : String(v);
}

function withUnit(n: number | null | undefined, unit: string): string | undefined {
  return n == null ? undefined : `${fmtNum(n)} ${unit}`;
}

// Street spellings that name one street: a valuation may carry "407 East Bay
// Street" where the stored profile carries "407 E Bay St".
const STREET_WORDS: Record<string, string> = {
  NORTH: 'N', SOUTH: 'S', EAST: 'E', WEST: 'W',
  STREET: 'ST', AVENUE: 'AVE', ROAD: 'RD', DRIVE: 'DR', BOULEVARD: 'BLVD',
  LANE: 'LN', COURT: 'CT', PLACE: 'PL', TERRACE: 'TER', CIRCLE: 'CIR',
  PARKWAY: 'PKWY', HIGHWAY: 'HWY',
};

function normalizeStreet(street: string): string {
  return street
    .toUpperCase()
    .replace(/[.,#]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => STREET_WORDS[word] ?? word)
    .join(' ');
}

const EXEMPTION_LABELS: Array<[keyof Omit<Exemptions, 'exemptionAmount'>, string]> = [
  ['homestead', 'Homestead'],
  ['veteran', 'Veteran'],
  ['senior', 'Senior'],
  ['disability', 'Disability'],
  ['widow', 'Widow'],
  ['agricultural', 'Agricultural'],
];

function fmtExemptions(val: Exemptions | undefined): string {
  if (!val) return '--';
  const names = EXEMPTION_LABELS.filter(([key]) => val[key] === true).map(([, label]) => label);
  const amount = val.exemptionAmount != null ? fmtCurrency(val.exemptionAmount) : null;
  const parts = [...names, ...(amount ? [amount] : [])];
  return parts.length > 0 ? parts.join(', ') : '--';
}

// Every ATTOM response the profile fetch can store, in the contract's order
// (AttomPropertyProfile.sources). `satisfies` makes a source the contract adds
// a tsc error here until it has a label.
const SOURCE_LABELS = {
  expandedProfile: 'Expanded profile',
  attomAvm: 'ATTOM AVM',
  salesHistory: 'Sales history',
  salesExpandedHistory: 'Sales expanded history',
  saleDetail: 'Sale detail',
  basicProfile: 'Basic profile',
  detailWithSchools: 'Detail with schools',
  mortgageOwnerDetail: 'Mortgage and owner detail',
  assessmentHistory: 'Assessment history',
  allEvents: 'All events',
  allEventsSnapshot: 'All events snapshot',
  rentalAvm: 'Rental AVM',
  salesComparables: 'Sales comparables',
  community: 'Neighborhood community',
  pointsOfInterest: 'Points of interest',
  preforeclosure: 'Preforeclosure',
  buildingPermits: 'Building permits',
  avmHistory: 'AVM history',
  homeEquity: 'Home equity',
  schoolDetail: 'School detail',
  schoolSnapshot: 'School snapshot',
} satisfies Record<Extract<keyof NonNullable<Profile['sources']>, string>, string>;

const SOURCE_KEYS = Object.keys(SOURCE_LABELS) as Array<keyof typeof SOURCE_LABELS>;

function storedCount(p: Profile): number {
  return SOURCE_KEYS.filter((k) => p.sources?.[k] != null).length;
}

// The dossier's four folios, in reading order. Sources, the raw ATTOM
// responses, is an Aaim-operator folio (GH #678 ruling 2026-10-23: the ATTOM
// folio is operator-only); FI staff read the first three.
const FOLIOS = ['brief', 'property', 'history', 'sources'] as const;
const FI_FOLIOS = ['brief', 'property', 'history'] as const;
const FOLIO_LABELS = {
  brief: 'Brief',
  property: 'Property',
  history: 'History',
  sources: 'Sources',
} satisfies Record<(typeof FOLIOS)[number], string>;

function isFolio(v: string | null): v is (typeof FOLIOS)[number] {
  return v != null && (FOLIOS as readonly string[]).includes(v);
}

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-selection focus-visible:ring-offset-2';
// A neutral text control; red is kept for the Search CTA and error alerts.
const QUIET = `inline-flex min-h-11 items-center gap-1 text-left underline decoration-recessed underline-offset-4 hover:text-primary ${FOCUS}`;

function Sep() {
  return (
    <>
      <span aria-hidden="true" className="mx-1.5 text-quaternary">·</span>
      <VisuallyHidden>, </VisuallyHidden>
    </>
  );
}

function Joined({ parts }: { parts: ReactNode[] }) {
  return (
    <>
      {parts.map((part, i) => (
        <Fragment key={i}>
          {i > 0 && (
            <>
              <Sep />
              <wbr />
            </>
          )}
          <span className="whitespace-nowrap">{part}</span>
        </Fragment>
      ))}
    </>
  );
}

function listNames(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** The ZIP search and result list; rendered in the rail and in the ZIP sheet. */
function ProfileSearch({
  idPrefix,
  intro,
  zipInput,
  onZipInput,
  onSubmit,
  isSearching,
  searchError,
  emptyZip,
  results,
  selectedId,
  onSelect,
}: {
  idPrefix: string;
  intro?: boolean;
  zipInput: string;
  onZipInput: (v: string) => void;
  onSubmit: () => void;
  isSearching: boolean;
  searchError: string | null;
  emptyZip: string | null;
  results: PropertyProfileResponse[];
  selectedId: string | null;
  onSelect: (attomId: string) => void;
}) {
  return (
    <div>
      {intro && (
        <p className="mb-4 text-body-sm text-secondary">Stored ATTOM property profiles, by ZIP code.</p>
      )}
      <form
        role="search"
        aria-label="Stored property profiles"
        className="flex items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <div className="min-w-0 flex-1">
          <Input
            id={`pp-zip-${idPrefix}`}
            name="zipCode"
            label="ZIP code"
            inputMode="numeric"
            autoComplete="postal-code"
            enterKeyHint="search"
            maxLength={5}
            placeholder="e.g. 94102"
            value={zipInput}
            onChange={(e) => onZipInput(e.target.value)}
          />
        </div>
        <Button
          type="submit"
          variant="primary"
          size="large"
          disabled={isSearching || !zipInput.trim()}
          aria-busy={isSearching}
        >
          {isSearching ? 'Searching' : 'Search'}
        </Button>
      </form>
      {searchError && (
        <Alert type="error" className="mt-4">
          {searchError}
        </Alert>
      )}
      {emptyZip && (
        <EmptyState
          title={`No stored profiles in ZIP ${emptyZip}`}
          description="A profile is stored when a property in this ZIP code is valued."
          className="py-8"
        />
      )}
      {results.length > 0 && (
        <>
          <Heading level={3} size="compact" className="mt-8">
            {`${results.length} result${results.length !== 1 ? 's' : ''}`}
          </Heading>
          <ul className="mt-2 border-t border-recessed">
            {results.map((r) => {
              const open = r.attomId === selectedId;
              return (
                <li key={r.attomId}>
                  <button
                    type="button"
                    aria-current={open ? 'true' : undefined}
                    onClick={() => onSelect(r.attomId)}
                    className={`flex w-full min-h-14 items-center justify-between gap-3 border-b border-recessed py-3 text-left hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-selection ${
                      open ? 'border-l-2 border-l-selection pl-3' : ''
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-body-md font-semibold text-primary">{r.address1}</span>
                      <span className="block truncate text-caption-md text-tertiary">
                        <Joined parts={[r.address2, `stored ${fmtDateUS(r.fetchedAt)}`]} />
                      </span>
                    </span>
                    {open ? (
                      <span className="shrink-0 text-caption-md text-tertiary">Open</span>
                    ) : (
                      <ChevronRight aria-hidden="true" className="h-4 w-4 shrink-0 text-tertiary" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

/** Pages the dossier in reading order from the end of a panel. */
function FolioNext({
  target,
  back,
  caption,
  onGo,
}: {
  target: (typeof FOLIOS)[number];
  back?: boolean;
  caption?: string;
  onGo: () => void;
}) {
  const kicker = back ? 'Back to' : 'Next';
  const Icon = back ? ChevronUp : ChevronRight;
  return (
    <div className="mt-12 border-t border-recessed lg:col-span-12">
      <button
        type="button"
        onClick={onGo}
        aria-label={`${kicker}: ${FOLIO_LABELS[target]}${caption ? `. ${caption}` : ''}`}
        className={`group flex w-full min-h-14 items-center justify-between gap-4 py-4 text-left ${FOCUS}`}
      >
        <span className="min-w-0">
          <span className="block text-label-xs font-semibold uppercase tracking-wider text-tertiary">{kicker}</span>
          <span className="mt-1 block font-serif text-heading-card text-primary group-hover:underline">
            {FOLIO_LABELS[target]}
          </span>
          {caption && (
            <span className="mt-1 block text-caption-md tabular-nums text-tertiary">{caption}</span>
          )}
        </span>
        <Icon aria-hidden="true" className="h-5 w-5 shrink-0 text-tertiary group-hover:text-primary" />
      </button>
    </div>
  );
}

function GroupHeading({ id, children }: { id: string; children: ReactNode }) {
  return (
    <SectionLabel size="sm" withMargin={false} id={id} role="heading" aria-level={3} className="mb-3">
      {children}
    </SectionLabel>
  );
}

function BriefPanel({
  p,
  onFolio,
  showSources,
}: {
  p: Profile;
  onFolio: (next: (typeof FOLIOS)[number]) => void;
  showSources: boolean;
}) {
  const v = p.valuation;
  const c = p.characteristics;
  const stored = storedCount(p);

  const confidence: string[] = [];
  if (v.confidenceScore != null) confidence.push(`${Math.round(v.confidenceScore * 100)}% confidence`);
  // Written out: the vendor abbreviation 'FSD' carried no expansion for a
  // reader or a screen reader (UI run r3, fi-g2-5).
  if (v.fsd != null) confidence.push(`forecast standard deviation ${fmtNum(Math.round(v.fsd * 10) / 10)}%`);
  if (v.avm != null && (v.avmLow == null || v.avmHigh == null)) confidence.push('range not reported');

  const saleYear = p.lastSale.date ? new Date(`${p.lastSale.date.slice(0, 10)}T00:00:00Z`).getUTCFullYear() : null;
  const anchors: Array<{ key: string; label: string; value: number; shape: 'square' | 'ring' }> = [];
  if (v.assessedValue != null) {
    anchors.push({ key: 'assessed', label: v.taxYear ? `Assessed ${v.taxYear}` : 'Assessed', value: v.assessedValue, shape: 'square' });
  }
  if (p.lastSale.amount != null) {
    anchors.push({ key: 'sale', label: saleYear && !isNaN(saleYear) ? `${saleYear} sale` : 'Last sale', value: p.lastSale.amount, shape: 'ring' });
  }

  const facts = [
    str(c.propertyType),
    c.yearBuilt != null ? `built ${String(c.yearBuilt)}` : undefined,
    c.bedrooms != null ? `${c.bedrooms} bed` : undefined,
    c.bathroomsTotal != null ? `${fmtNum(c.bathroomsTotal)} bath` : undefined,
    c.livingSize != null ? `${fmtNum(c.livingSize)} sq ft` : undefined,
    (c.lotSize ?? c.lotAcres) != null ? `${fmtNum((c.lotSize ?? c.lotAcres) as number)}-acre lot` : undefined,
  ].filter((f): f is string => f != null);

  const r = p.rentalValuation;
  const e = p.homeEquity;
  const ledger: Array<{ label: string; value: string; change?: string; changeType?: 'neutral' }> = [];
  if (v.assessedValue != null) ledger.push({ label: v.taxYear ? `Assessed, ${v.taxYear}` : 'Assessed', value: fmtCurrency(v.assessedValue) });
  if (v.taxAmount != null) ledger.push({ label: v.taxYear ? `Property tax, ${v.taxYear}` : 'Property tax', value: fmtCurrency(v.taxAmount) });
  if (v.marketValue != null) ledger.push({ label: 'Assessor market value', value: fmtCurrency(v.marketValue) });
  if (p.lastSale.amount != null) {
    ledger.push({ label: p.lastSale.date ? `Last sale, ${fmtDateUS(p.lastSale.date)}` : 'Last sale', value: fmtCurrency(p.lastSale.amount) });
  }
  if (r?.rentAmount != null) {
    ledger.push({
      label: 'Rent, monthly',
      value: fmtCurrency(r.rentAmount),
      ...(r.rentAmountLow != null && r.rentAmountHigh != null
        ? { change: `${usdCompact(r.rentAmountLow)}–${usdCompact(r.rentAmountHigh)}`, changeType: 'neutral' as const }
        : {}),
    });
  }
  if (e?.estimatedEquity != null) {
    ledger.push({
      label: 'Estimated equity',
      value: fmtCurrency(e.estimatedEquity),
      ...(e.equityPercent != null ? { change: fmtPts(e.equityPercent), changeType: 'neutral' as const } : {}),
    });
  }
  if (e?.combinedLTV != null) ledger.push({ label: 'Combined LTV', value: fmtPts(e.combinedLTV) });
  if (e?.underwaterIndicator != null) ledger.push({ label: 'Underwater', value: e.underwaterIndicator ? 'Yes' : 'No' });

  const absent = [
    p.rentalValuation == null ? 'rent estimate' : null,
    p.homeEquity == null ? 'home equity' : null,
    p.mortgage == null ? 'recorded liens' : null,
    p.ownership == null ? 'owner of record' : null,
  ].filter((n): n is string => n != null);

  const roll = (p.assessmentHistory ?? [])
    .map((a) => ({ year: Number(a.year), total: a.assessedTotal }))
    .filter((a): a is { year: number; total: number } => Number.isFinite(a.year) && typeof a.total === 'number' && Number.isFinite(a.total))
    .sort((a, b) => a.year - b.year);
  const first = roll[0];
  const last = roll[roll.length - 1];
  const rollSummary =
    roll.length >= 2
      ? `Assessed value from ${fmtCurrency(first.total)} in ${first.year} to ${fmtCurrency(last.total)} in ${last.year}.`
      : '';

  return (
    <div className="lg:grid lg:grid-cols-12 lg:gap-x-12">
      <div className="lg:col-span-5">
        <SectionLabel size="xs" withMargin={false}>
          <Joined parts={['ATTOM AVM', ...(v.avmDate ? [fmtDateUS(v.avmDate)] : [])]} />
        </SectionLabel>
        {v.avm != null ? (
          <p className="mt-2 font-serif text-stat font-semibold tabular-nums text-primary lg:text-stat-md">
            <VisuallyHidden>Automated valuation </VisuallyHidden>
            {fmtCurrency(v.avm)}
          </p>
        ) : (
          <p className="mt-2 font-serif text-heading-card text-tertiary">No AVM stored</p>
        )}
        {confidence.length > 0 && (
          <p className="mt-2 font-mono text-caption-md tabular-nums text-tertiary">
            <Joined parts={confidence} />
          </p>
        )}
        <ValueRule avm={v.avm} low={v.avmLow} high={v.avmHigh} anchors={anchors} />
        {facts.length > 0 && (
          <p className="mt-5 text-body-md text-secondary">
            <Joined parts={facts} />
          </p>
        )}
      </div>
      <div className="lg:col-span-7">
        {ledger.length > 0 && (
          <div data-testid="pp-ledger" className="mt-6 lg:mt-0">
            <MetricLedgerBlock items={ledger} valueFont="mono" />
          </div>
        )}
        {absent.length > 0 && (
          <p className="mt-3 text-caption-md text-secondary">
            {`Not in this profile: ${listNames(absent)}.`}
            {showSources && stored > 0 && (
              <>
                {' '}
                <button type="button" className={`${QUIET} text-caption-md text-secondary`} onClick={() => onFolio('sources')}>
                  Raw ATTOM responses
                </button>
              </>
            )}
          </p>
        )}
        {roll.length >= 2 && (
          <button
            type="button"
            aria-label={`Open history. ${rollSummary}`}
            onClick={() => onFolio('history')}
            className={`mt-6 block w-full min-h-11 rounded-sm text-left ${FOCUS}`}
          >
            <span className="flex items-baseline justify-between text-caption-md text-tertiary">
              <span>Assessed value by roll year</span>
              <span className="font-mono tabular-nums">{`${first.year}–${last.year}`}</span>
            </span>
            <InlineSparkline values={roll.map((a) => a.total)} className="mt-1 h-10 w-full lg:h-14" />
            <span className="mt-1 flex justify-between font-mono text-caption-sm tabular-nums text-tertiary">
              <span>{usdCompact(first.total)}</span>
              <span>{usdCompact(last.total)}</span>
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

function SpecSheet({
  id,
  title,
  noun,
  fields,
  total,
  cap,
  expanded,
  onToggle,
  wide,
  children,
}: {
  id: string;
  title: string;
  noun: string;
  fields: Array<{ label: string; value: string; detail?: string }>;
  total: number;
  cap?: number;
  expanded?: boolean;
  onToggle?: () => void;
  wide?: boolean;
  children?: ReactNode;
}) {
  const capped = cap != null && fields.length > cap;
  return (
    <section aria-labelledby={`${id}-h`}>
      <GroupHeading id={`${id}-h`}>{title}</GroupHeading>
      <dl id={id} className={`grid grid-cols-2 gap-x-6 ${wide ? 'sm:grid-cols-3' : ''}`}>
        {fields.map((f, i) => (
          <div
            key={f.label}
            className={`min-w-0 border-t border-recessed pb-3 pt-2 ${capped && !expanded && i >= (cap as number) ? 'hidden md:block' : ''}`}
          >
            <dt className="text-caption-md text-tertiary">{f.label}</dt>
            <dd className="mt-0.5 break-words text-body-sm tabular-nums text-primary">
              {f.value}
              {f.detail && <span className="block text-caption-md text-tertiary">{f.detail}</span>}
            </dd>
          </div>
        ))}
      </dl>
      {capped && onToggle && (
        <button
          type="button"
          aria-expanded={Boolean(expanded)}
          aria-controls={id}
          onClick={onToggle}
          className={`${QUIET} mt-2 text-body-sm text-secondary md:hidden`}
        >
          {expanded ? 'Show fewer' : `Show all ${fields.length} ${noun} fields`}
        </button>
      )}
      {children}
      {fields.length < total && (
        <p className="mt-2 text-caption-md text-tertiary">
          {`Not in the stored record: ${total - fields.length} of ${total} ${noun} fields.`}
        </p>
      )}
    </section>
  );
}

function field(label: string, value: string | undefined, detail?: string) {
  return value == null ? null : { label, value, ...(detail ? { detail } : {}) };
}

function presentFields(list: Array<ReturnType<typeof field>>) {
  return list.filter((f): f is NonNullable<ReturnType<typeof field>> => f != null);
}

// The Property folio's three spec-sheet groups; the Brief's Next caption counts
// the same fields the panel shows.
function propertyGroups(p: Profile) {
  const c = p.characteristics;
  const cl = p.classification;
  const u = p.utilities;
  const a = p.address;
  const buildingAll = [
    field('Property type', str(c.propertyType)),
    field('Year built', c.yearBuilt != null ? String(c.yearBuilt) : undefined),
    field('Living area', withUnit(c.livingSize, 'sq ft')),
    field('Bedrooms', str(c.bedrooms)),
    field(
      'Bathrooms',
      c.bathroomsTotal != null ? fmtNum(c.bathroomsTotal) : undefined,
      c.bathroomsFull != null && c.bathroomsHalf != null ? `${c.bathroomsFull} full, ${c.bathroomsHalf} half` : undefined,
    ),
    field('Stories', str(c.stories)),
    field('Garage', str(c.garageType), withUnit(c.garageSize, 'sq ft')),
    field('Condition', str(c.constructionCondition)),
    field('Pool', str(c.poolType)),
    field('Quality', str(c.quality)),
    field('Subtype', str(c.propertySubtype)),
    field('Effective year', c.yearBuiltEffective != null ? String(c.yearBuiltEffective) : undefined),
    field('Gross area', withUnit(c.grossSize, 'sq ft')),
    field('Rooms', str(c.roomsTotal)),
    field('Parking spaces', str(c.parkingSpaces)),
    field('Carport spaces', str(c.carportSpaces)),
    field('Fireplaces', str(c.fireplaceCount), str(c.fireplaceType)),
    field('Basement', str(c.basementType), withUnit(c.basementSize, 'sq ft')),
    field('Style', str(c.architecturalStyle)),
    field('Frame', str(c.frameType)),
    field('Walls', str(c.wallType)),
    field('Roof', str(c.roofType), str(c.roofCover)),
    field('Foundation', str(c.foundationType)),
    field('Floors', str(c.floorCovering)),
    field('View', str(c.view)),
  ];
  const lotAll = [
    field('Lot size', withUnit(c.lotSize ?? c.lotAcres, 'acres')),
    field('Land use', str(cl.landUse)),
    field('Property class', str(cl.propertyClass)),
    field('Zoning', str(cl.zoningCode)),
    field('Units', str(cl.unitsCount)),
    field('Buildings', str(cl.buildingCount)),
    field('County', str(a.county)),
    field('Census tract', str(a.censusTract)),
    field('Census block', str(a.censusBlock)),
    field('APN', str(p.apn)),
    field('FIPS', str(p.fips)),
    field('ATTOM ID', String(p.attomId)),
  ];
  const systemsAll = [
    field('Cooling', str(u.coolingType)),
    field('Heating', str(u.heatingType), str(u.heatingFuel)),
    field('Sewer', str(u.sewerType)),
    field('Water', str(u.waterSource)),
    field('Electric', str(u.electricType)),
    field('Gas', str(u.gasType)),
  ];
  return {
    building: presentFields(buildingAll),
    buildingTotal: buildingAll.length,
    lot: presentFields(lotAll),
    lotTotal: lotAll.length,
    systems: presentFields(systemsAll),
    systemsTotal: systemsAll.length,
  };
}

// Assessment rows with a numeric roll year, newest first. A year can appear
// twice (ATTOM reports more than one assessment for a roll year).
function rollRows(p: Profile) {
  return (p.assessmentHistory ?? [])
    .filter((a) => Number.isFinite(Number(a.year)))
    .sort((a, b) => Number(b.year) - Number(a.year));
}

function PropertyPanel({
  p,
  buildingExpanded,
  onToggleBuilding,
  onFolio,
}: {
  p: Profile;
  buildingExpanded: boolean;
  onToggleBuilding: () => void;
  onFolio: (next: (typeof FOLIOS)[number]) => void;
}) {
  const cl = p.classification;
  const { building, buildingTotal, lot, lotTotal, systems, systemsTotal } = propertyGroups(p);

  const o = p.ownership;
  const owner = o
    ? presentFields([
        field('Owner', str(o.owner1?.fullName)),
        field('Co-owner', str(o.owner2?.fullName)),
        field('Vesting', str(o.vestingType)),
        field('Occupancy', str(o.occupancyStatus)),
        field('Owner type', str(o.ownerType)),
        field('Corporate', o.corporateOwner == null ? undefined : o.corporateOwner ? 'Yes' : 'No'),
        field('Absentee', o.absenteeOwner == null ? undefined : o.absenteeOwner ? 'Yes' : 'No'),
        field(
          'Trust',
          o.trustIndicator == null ? undefined : o.trustIndicator ? `Yes${o.trustDescription ? `, ${o.trustDescription}` : ''}` : 'No',
        ),
        field(
          'Transferred',
          o.ownershipTransferDate || o.ownershipTransferAmount != null
            ? [o.ownershipTransferDate ? fmtDateUS(o.ownershipTransferDate) : null, o.ownershipTransferAmount != null ? fmtCurrency(o.ownershipTransferAmount) : null]
                .filter(Boolean)
                .join(', ')
            : undefined,
        ),
        field('Exemptions', fmtExemptions(o.exemptions) === '--' ? undefined : fmtExemptions(o.exemptions)),
        field('Mailing', str(o.mailingAddress?.formatted)),
      ])
    : [];

  const liens = (['first', 'second', 'third'] as const)
    .map((pos, i) => ({ pos: ['1st', '2nd', '3rd'][i], m: p.mortgage?.[pos] }))
    .filter((l) => l.m != null);
  const schools = p.schools ?? [];

  const omitted = [
    building.length === 0 ? 'building characteristics' : null,
    lot.length === 0 ? 'lot and location' : null,
    systems.length === 0 ? 'systems' : null,
    owner.length === 0 ? 'owner of record' : null,
    liens.length === 0 ? 'recorded liens' : null,
    schools.length === 0 ? 'nearby schools' : null,
  ].filter((n): n is string => n != null);

  return (
    <div className="space-y-10 lg:grid lg:grid-cols-12 lg:gap-x-12 lg:gap-y-10 lg:space-y-0">
      {building.length > 0 && (
        <div className="lg:col-span-7">
          <SpecSheet
            id="pp-building"
            title="Building"
            noun="building"
            fields={building}
            total={buildingTotal}
            cap={8}
            expanded={buildingExpanded}
            onToggle={onToggleBuilding}
            wide
          />
        </div>
      )}
      <div className="space-y-10 lg:col-span-5">
        {lot.length > 0 && (
          <SpecSheet id="pp-lot" title="Lot and location" noun="lot and location" fields={lot} total={lotTotal}>
            {[
              ['Zoning description', str(cl.zoningDescription)],
              ['Legal description', str(cl.legalDescription)],
            ]
              .filter((d): d is [string, string] => d[1] != null)
              .map(([label, text]) => (
                <div key={label} className="mt-3">
                  <p className="text-caption-md text-tertiary">{label}</p>
                  <p className="break-words text-body-sm text-secondary">{text}</p>
                </div>
              ))}
          </SpecSheet>
        )}
        {systems.length > 0 && (
          <SpecSheet id="pp-systems" title="Systems" noun="systems" fields={systems} total={systemsTotal} />
        )}
        {owner.length > 0 && (
          <section aria-labelledby="pp-owner-h">
            <GroupHeading id="pp-owner-h">Owner of record</GroupHeading>
            <dl className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-4 gap-y-2">
              {owner.map((f) => (
                <div key={f.label} className="contents">
                  <dt className="text-caption-md text-tertiary">{f.label}</dt>
                  <dd className="break-words text-body-sm text-primary">{f.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}
        {liens.length > 0 && (
          <section aria-labelledby="pp-liens-h">
            <GroupHeading id="pp-liens-h">Recorded liens</GroupHeading>
            <p className="mb-2 text-caption-md text-tertiary">Original recorded amounts, not balances.</p>
            <ul>
              {liens.map(({ pos, m }) => (
                <li key={pos} className="grid grid-cols-[2.5rem_minmax(0,1fr)_auto] gap-x-3 border-b border-recessed py-2.5">
                  <span className="text-label-xs uppercase text-tertiary">{pos}</span>
                  <span className="truncate text-body-sm text-primary">{m?.lenderName ?? 'Lender not reported'}</span>
                  <span className="font-mono text-body-sm tabular-nums text-primary">{m?.amount != null ? fmtCurrency(m.amount) : <Absent />}</span>
                  <span className="col-span-2 col-start-2 text-caption-md text-tertiary">
                    <Joined
                      parts={[
                        str(m?.loanType),
                        m?.rate != null ? fmtPts(m.rate) : undefined,
                        str(m?.rateType),
                        m?.date ? `recorded ${fmtDateUS(m.date)}` : undefined,
                        m?.maturityDate ? `matures ${fmtDateUS(m.maturityDate)}` : undefined,
                        m?.equityLineOfCredit ? 'HELOC' : undefined,
                        m?.refiFlag ? 'Refinance' : undefined,
                      ].filter((x): x is string => x != null)}
                    />
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
        {schools.length > 0 && (
          <section aria-labelledby="pp-schools-h">
            <GroupHeading id="pp-schools-h">Nearby schools</GroupHeading>
            <ul>
              {schools.map((s, i) => (
                <li key={`${s.name ?? 'school'}-${i}`} className="border-b border-recessed py-2.5">
                  <span className="block text-body-sm text-primary">{s.name ?? 'Name not reported'}</span>
                  <span className="block text-caption-md text-tertiary">
                    <Joined
                      parts={[str(s.schoolType), str(s.gradeLevel), str(s.district), str(s.distance)].filter(
                        (x): x is string => x != null,
                      )}
                    />
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
      {omitted.length > 0 && (
        <p className="text-caption-md text-secondary lg:col-span-12">{`Not in this profile: ${listNames(omitted)}.`}</p>
      )}
      <FolioNext
        target="history"
        caption={(() => {
          const rolls = rollRows(p).length;
          const events = historyEvents(p).length;
          return (
            [rolls ? `${rolls} assessment rows` : null, events ? `${events} recorded event${events !== 1 ? 's' : ''}` : null]
              .filter(Boolean)
              .join(' · ') || undefined
          );
        })()}
        onGo={() => onFolio('history')}
      />
    </div>
  );
}

// Sales, recorded loans and permits merged into one list, newest first.
function historyEvents(p: Profile) {
  const events: Array<{ date: string; kind: string; detail?: string; amount?: number }> = [];
  const saleDates = new Set<string>();
  const sales = [
    ...(p.lastSale.date ? [{ date: p.lastSale.date, amount: p.lastSale.amount, doc: p.lastSale.documentType ?? p.lastSale.transactionType }] : []),
    ...(p.salesHistory ?? []).map((s) => ({ date: s.date, amount: s.amount, doc: s.documentType ?? s.transactionType })),
  ];
  for (const s of sales) {
    if (!s.date) continue;
    const key = s.date.slice(0, 10);
    if (saleDates.has(key)) continue;
    saleDates.add(key);
    events.push({ date: s.date, kind: 'Sale', detail: str(s.doc), amount: s.amount ?? undefined });
  }
  for (const m of p.mortgageHistory ?? []) {
    const date = m.recordingDate ?? m.date;
    if (!date) continue;
    const detail = [str(m.lenderName), str(m.loanType), 'original amount'].filter(Boolean).join(', ');
    events.push({ date, kind: 'Mortgage', detail, amount: m.amount ?? undefined });
  }
  for (const b of p.buildingPermits ?? []) {
    if (!b.permitDate) continue;
    const detail = [str(b.permitType), str(b.permitStatus), str(b.description)].filter(Boolean).join(', ');
    events.push({ date: b.permitDate, kind: 'Permit', detail: detail || undefined, amount: b.permitValue ?? undefined });
  }
  return events.sort((x, y) => (x.date < y.date ? 1 : x.date > y.date ? -1 : 0));
}

function HistoryPanel({
  p,
  rollExpanded,
  onToggleRoll,
  onFolio,
  showSources,
}: {
  p: Profile;
  rollExpanded: boolean;
  onToggleRoll: () => void;
  onFolio: (next: (typeof FOLIOS)[number]) => void;
  showSources: boolean;
}) {
  const v = p.valuation;
  const rolls = rollRows(p);
  const hasMarket = rolls.some((a) => a.marketTotal != null);
  const events = historyEvents(p);
  const avmHistory = p.avmHistory ?? [];

  const assessments = rolls
    .filter((a) => a.assessedTotal != null)
    .map((a) => ({ x: Number(a.year), total: a.assessedTotal as number }));
  const sales = events
    .filter((e) => e.kind === 'Sale' && e.amount != null)
    .map((e) => ({ x: yearFraction(e.date), amount: e.amount as number, label: `${e.date.slice(0, 4)} sale` }))
    .filter((s): s is { x: number; amount: number; label: string } => s.x != null);
  const avmX = v.avmDate ? yearFraction(v.avmDate) : null;
  const avmPoint =
    v.avm != null && avmX != null
      ? { x: avmX, value: v.avm, ...(v.avmLow != null ? { low: v.avmLow } : {}), ...(v.avmHigh != null ? { high: v.avmHigh } : {}) }
      : undefined;
  const histPoints = avmHistory
    .map((h) => ({ x: h.date ? yearFraction(h.date) : null, value: h.value }))
    .filter((h): h is { x: number; value: number } => h.x != null && typeof h.value === 'number');
  const plotted = assessments.length + sales.length + (avmPoint ? 1 : 0) + (histPoints.length > 1 ? histPoints.length : 0);
  const summary = [
    assessments.length >= 2
      ? `Assessed value ${usdCompact(assessments[assessments.length - 1].total)} in ${assessments[assessments.length - 1].x}, ${usdCompact(assessments[0].total)} in ${assessments[0].x}.`
      : '',
    ...sales.map((s) => `${s.label} ${usdCompact(s.amount)}.`),
    avmPoint ? `ATTOM AVM ${usdCompact(avmPoint.value)}.` : '',
  ]
    .filter(Boolean)
    .join(' ');

  const empty = [
    avmHistory.length === 0 ? 'AVM history' : null,
    (p.salesHistory ?? []).length === 0 ? 'sales history' : null,
    (p.mortgageHistory ?? []).length === 0 ? 'recorded loans' : null,
    (p.buildingPermits ?? []).length === 0 ? 'building permits' : null,
    rolls.length === 0 ? 'assessment roll' : null,
  ].filter((n): n is string => n != null);

  const th = 'py-2 font-sans text-label-xs font-semibold uppercase tracking-wider text-tertiary';
  const td = 'py-2.5 font-mono text-body-sm tabular-nums text-primary';
  const cell = (n: number | null | undefined) => (n == null ? <Absent /> : fmtCurrency(n));

  return (
    <div className="space-y-10">
      {plotted >= 2 && (
        <section aria-labelledby="pp-chart-h">
          <GroupHeading id="pp-chart-h">Value over time</GroupHeading>
          <ValueHistoryChart
            assessments={assessments}
            sales={sales}
            avm={avmPoint}
            avmHistory={histPoints.length > 1 ? histPoints : []}
            summary={summary}
          />
        </section>
      )}
      <div className="space-y-10 lg:grid lg:grid-cols-12 lg:gap-x-12 lg:space-y-0">
        {rolls.length > 0 && (
          <section aria-labelledby="pp-roll-h" className="lg:col-span-7">
            <GroupHeading id="pp-roll-h">Assessment roll</GroupHeading>
            <table className="w-full">
              <caption>
                <VisuallyHidden>{`Assessment history, ${rolls.length} rows, newest first`}</VisuallyHidden>
              </caption>
              <thead className="border-b-2 border-primary">
                <tr>
                  <th scope="col" className={`${th} text-left`}>Year</th>
                  <th scope="col" className={`${th} text-right`}>Assessed</th>
                  <th scope="col" className={`${th} text-right`}>Tax</th>
                  <th scope="col" className={`${th} hidden text-right lg:table-cell`}>Land</th>
                  <th scope="col" className={`${th} hidden text-right lg:table-cell`}>Improvement</th>
                  {hasMarket && <th scope="col" className={`${th} hidden text-right lg:table-cell`}>Market</th>}
                </tr>
              </thead>
              <tbody id="pp-roll">
                {rolls.map((a, i) => (
                  <tr
                    key={`${a.year}-${i}`}
                    className={`border-b border-recessed ${i >= 5 && !rollExpanded ? 'hidden lg:table-row' : ''}`}
                  >
                    <td className={td}>{String(a.year)}</td>
                    <td className={`${td} text-right`}>{cell(a.assessedTotal)}</td>
                    <td className={`${td} text-right`}>{cell(a.taxAmount)}</td>
                    <td className={`${td} hidden text-right lg:table-cell`}>{cell(a.assessedLand)}</td>
                    <td className={`${td} hidden text-right lg:table-cell`}>{cell(a.assessedImprovement)}</td>
                    {hasMarket && <td className={`${td} hidden text-right lg:table-cell`}>{cell(a.marketTotal)}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
            {rolls.length > 5 && (
              <button
                type="button"
                aria-expanded={rollExpanded}
                aria-controls="pp-roll"
                onClick={onToggleRoll}
                className={`${QUIET} mt-2 text-body-sm text-secondary lg:hidden`}
              >
                {rollExpanded ? 'Show latest 5 rows' : `Show all ${rolls.length} rows`}
              </button>
            )}
          </section>
        )}
        {events.length > 0 && (
          <section aria-labelledby="pp-events-h" className="lg:col-span-5">
            <GroupHeading id="pp-events-h">Recorded events</GroupHeading>
            <ol>
              {events.map((e, i) => (
                <li
                  key={`${e.kind}-${e.date}-${i}`}
                  className="grid grid-cols-[5.5rem_minmax(0,1fr)_auto] items-baseline gap-x-3 border-b border-recessed py-2.5"
                >
                  <span className="font-mono text-caption-md tabular-nums text-tertiary">{fmtDateUS(e.date)}</span>
                  <span className="text-body-sm text-primary">
                    {e.kind}
                    {e.detail && <span className="block text-caption-md text-tertiary">{e.detail}</span>}
                  </span>
                  <span className="font-mono text-body-sm tabular-nums text-primary">
                    {e.amount != null ? fmtCurrency(e.amount) : ''}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>
      {avmHistory.length > 0 && (
        <section aria-labelledby="pp-avmh-h">
          <GroupHeading id="pp-avmh-h">AVM history</GroupHeading>
          <table className="w-full">
            <caption>
              <VisuallyHidden>{`AVM history, ${avmHistory.length} rows`}</VisuallyHidden>
            </caption>
            <thead className="border-b-2 border-primary">
              <tr>
                <th scope="col" className={`${th} text-left`}>Date</th>
                <th scope="col" className={`${th} text-right`}>Value</th>
                <th scope="col" className={`${th} text-right`}>Range</th>
                <th scope="col" className={`${th} hidden text-right lg:table-cell`}>Confidence</th>
              </tr>
            </thead>
            <tbody>
              {avmHistory.map((h, i) => (
                <tr key={`${h.date ?? 'avm'}-${i}`} className="border-b border-recessed">
                  <td className={td}>{h.date ? fmtDateUS(h.date) : <Absent />}</td>
                  <td className={`${td} text-right`}>{cell(h.value)}</td>
                  <td className={`${td} text-right`}>
                    {h.valueLow != null && h.valueHigh != null ? `${usdCompact(h.valueLow)}–${usdCompact(h.valueHigh)}` : <Absent />}
                  </td>
                  <td className={`${td} hidden text-right lg:table-cell`}>
                    {h.confidenceScore != null ? `${Math.round(h.confidenceScore * 100)}%` : <Absent />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      {empty.length > 0 && (
        <p className="text-caption-md text-secondary">{`Not stored for this profile: ${listNames(empty)}.`}</p>
      )}
      {showSources ? (
        <FolioNext
          target="sources"
          caption={storedCount(p) > 0 ? `${storedCount(p)} of ${SOURCE_KEYS.length} ATTOM responses stored` : 'No raw responses stored'}
          onGo={() => onFolio('sources')}
        />
      ) : (
        <FolioNext target="brief" back onGo={() => onFolio('brief')} />
      )}
    </div>
  );
}

function SourcesPanel({
  p,
  onOpenJson,
  onFolio,
}: {
  p: Profile;
  onOpenJson: (key: keyof typeof SOURCE_LABELS | 'profile') => void;
  onFolio: (next: (typeof FOLIOS)[number]) => void;
}) {
  const storedKeys = SOURCE_KEYS.filter((k) => p.sources?.[k] != null);
  const missing = SOURCE_KEYS.filter((k) => p.sources?.[k] == null);
  const at = p.sourcesFetchedAt ?? {};
  const times = storedKeys.map((k) => at[k]).filter((t): t is number => typeof t === 'number');
  const min = times.length ? Math.min(...times) : null;
  const max = times.length ? Math.max(...times) : null;
  const sameDay = min != null && max != null && new Date(min).toDateString() === new Date(max).toDateString();

  return (
    <div>
      <p className="text-body-md text-primary">
        {storedKeys.length > 0
          ? `${storedKeys.length} of ${SOURCE_KEYS.length} ATTOM responses stored`
          : 'No raw ATTOM responses are stored with this profile.'}
      </p>
      {min != null && max != null && (
        <p className="mt-1 font-mono text-caption-md tabular-nums text-tertiary">
          {`Fetched ${fmtDateUS(new Date(min).toISOString())}, ${fmtTime(min)}`}
          {fmtTime(max) !== fmtTime(min) || !sameDay
            ? sameDay
              ? `–${fmtTime(max)}`
              : ` to ${fmtDateUS(new Date(max).toISOString())}, ${fmtTime(max)}`
            : ''}
        </p>
      )}
      {storedKeys.length > 0 && (
        <>
          <ul className="mt-4 border-t border-recessed lg:grid lg:grid-cols-2 lg:gap-x-10">
            {storedKeys.map((k) => (
              <li key={k}>
                <button
                  type="button"
                  onClick={() => onOpenJson(k)}
                  className={`flex w-full min-h-11 items-center justify-between gap-3 border-b border-recessed py-2.5 text-left hover:text-primary ${FOCUS}`}
                >
                  <span className="min-w-0 truncate text-body-sm text-primary">{SOURCE_LABELS[k]}</span>
                  <span className="flex shrink-0 items-center gap-2">
                    {typeof at[k] === 'number' && (
                      <span className="font-mono text-caption-md tabular-nums text-tertiary">{fmtTime(at[k] as number)}</span>
                    )}
                    <ChevronRight aria-hidden="true" className="h-4 w-4 text-tertiary" />
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {missing.length > 0 && (
            <>
              <p className="mt-4 text-body-sm text-secondary">
                {`Not stored: ${listNames(missing.map((k) => SOURCE_LABELS[k]))}.`}
              </p>
              <p className="mt-1 text-caption-md text-tertiary">
                A source is not stored when its call failed, returned no record, or is not on the ATTOM plan.
              </p>
            </>
          )}
        </>
      )}
      <button type="button" onClick={() => onOpenJson('profile')} className={`${QUIET} mt-6 text-body-sm text-secondary`}>
        Full profile JSON
        <ChevronRight aria-hidden="true" className="h-4 w-4" />
      </button>
      <FolioNext target="brief" back onGo={() => onFolio('brief')} />
    </div>
  );
}

// The address a valuation is requested for. The listing's own address comes
// first: address1 plus address2, which market-data writes as
// `${city}, ${state} ${zipCode}` from the address the valuation was requested
// with. platform-admin-api keys a valuation on a lowercase hash of that
// address, so ATTOM's normalized form ('407 E BAY ST') would add a second row
// beside '407 East Bay St' where Revalue says it replaces one. ATTOM's address
// is the fallback. A profile with neither has no address to request.
const ADDRESS2 = /^(.+?),\s*([A-Za-z]{2})\s+(\d{5})(?:-\d{4})?$/;

function valuationAddress(sel: PropertyProfileResponse): ValuationRequest['address'] | null {
  const listed = ADDRESS2.exec(sel.address2.trim());
  if (sel.address1 && listed) {
    return { street: sel.address1, city: listed[1], state: listed[2], zipCode: listed[3] };
  }
  const a = sel.profile.address;
  const street = a?.street ?? sel.address1;
  const zipCode = a?.zip ?? sel.zipCode;
  if (!street || !a?.city || !a?.state || !zipCode) return null;
  return { street, city: a.city, state: a.state, zipCode };
}

const VALUATION_STATUS_LABELS: Record<Valuation['status'], string> = {
  CERTIFIED: 'certified',
  PENDING: 'pending',
  NEEDS_REVIEW: 'needs review',
  FAILED: 'failed',
};

// A per-property action. Each one bills a provider, so each opens a confirm
// step that names the cost before any request is made.
function PropertyActions({
  address,
  busy,
  onRevalue,
}: {
  address: ValuationRequest['address'] | null;
  busy: boolean;
  onRevalue: () => void;
}) {
  return (
    <div role="group" aria-label="Property actions" className="mt-2 flex flex-wrap items-center gap-x-4">
      <button
        type="button"
        aria-haspopup="dialog"
        disabled={!address || busy}
        onClick={onRevalue}
        className={`${QUIET} text-body-sm font-semibold text-secondary disabled:text-disabled disabled:no-underline`}
      >
        {busy ? 'Revaluing...' : 'Revalue'}
      </button>
      {!address && (
        <span className="text-caption-md text-tertiary">
          Revalue needs a city and state, and this profile carries neither.
        </span>
      )}
    </div>
  );
}

// What the last revalue returned. Figures the valuation did not carry are left
// out, never filled.
function RevalueOutcome({ valuation, refreshed }: { valuation: Valuation; refreshed: boolean }) {
  const parts: string[] = [];
  if (valuation.certifiedValue != null) parts.push(fmtCurrency(valuation.certifiedValue));
  if (valuation.confidenceScore != null) parts.push(`confidence ${Math.round(valuation.confidenceScore * 100)}%`);
  parts.push(VALUATION_STATUS_LABELS[valuation.status]);
  return (
    <>
      Revalued: {parts.join(', ')}.{' '}
      {refreshed ? 'Stored ATTOM responses refreshed.' : 'Stored ATTOM responses unchanged.'}{' '}
      <Link to="/collateral/property-valuations" className={`${QUIET} text-caption-md`}>
        Property Valuations
      </Link>
    </>
  );
}

function DoneButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      className={`inline-flex min-h-11 items-center rounded-sm px-3 text-body-sm font-semibold text-secondary hover:text-primary ${FOCUS}`}
    >
      Done
    </button>
  );
}

/**
 * PropertyProfilePage
 *
 * A stored ATTOM property profile read as a collateral dossier: a Brief
 * (value, range, what the building is, the money figures), then Property,
 * History and Sources folios. Profiles are found by ZIP code; a Property
 * Valuations row deep-links with zipCode and street. Revalue, on the open
 * property, requests a new valuation after a confirm step that names the ATTOM
 * cost, then reloads the profile.
 *
 * Revalue and the Sources folio are offered to Aaim operators (a
 * platform-admin role) only: they spend and expose the operator's ATTOM
 * credentials (scope invariant 1; GH #678 ruling 2026-10-23). FI staff read
 * the Brief, Property and History folios, and a ?view=sources link opens the
 * Brief for them.
 *
 * Route: /collateral/property-profile[?zipCode=&street=&view=]
 * API:   GET /api/v1/property/profiles/search?zipCode=
 *        GET /api/v1/property/profiles/:attomId
 *        POST /api/v1/property/valuations
 */
export function PropertyProfilePage() {
  const isOperator = hasPlatformAdminRole(useAuthStore((s) => s.user)?.roles);
  const folios: ReadonlyArray<(typeof FOLIOS)[number]> = isOperator ? FOLIOS : FI_FOLIOS;
  const [searchParams, setSearchParams] = useSearchParams();
  const [zipInput, setZipInput] = useState('');
  const [results, setResults] = useState<PropertyProfileResponse[]>([]);
  const [selected, setSelected] = useState<PropertyProfileResponse | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [emptyZip, setEmptyZip] = useState<string | null>(null);
  const [folio, setFolio] = useState<(typeof FOLIOS)[number]>('brief');
  // The folio on screen: an FI session asked for Sources (by link or URL)
  // reads the Brief.
  const activeFolio = folios.includes(folio) ? folio : 'brief';
  const [zipSheetOpen, setZipSheetOpen] = useState(false);
  const [jsonKey, setJsonKey] = useState<keyof typeof SOURCE_LABELS | 'profile' | null>(null);
  const [rollExpanded, setRollExpanded] = useState(false);
  const [buildingExpanded, setBuildingExpanded] = useState(false);
  const [revalueOpen, setRevalueOpen] = useState(false);
  const [revaluingId, setRevaluingId] = useState<string | null>(null);
  // Keyed by attomId, so an outcome shows only on the property it belongs to.
  const [revalueOutcome, setRevalueOutcome] = useState<
    { attomId: string; valuation: Valuation; refreshed: boolean } | { attomId: string; error: string } | null
  >(null);

  const confirmRevalue = useCallback(async () => {
    if (!selected) return;
    const address = valuationAddress(selected);
    if (!address) return;
    const { attomId, fetchedAt: before } = selected;
    setRevalueOpen(false);
    setRevaluingId(attomId);
    setRevalueOutcome(null);
    try {
      const valuation = await propertyValuationsApi.create({ address });
      const fresh = await propertyProfileApi.getByAttomId(attomId);
      if (fresh) setSelected((cur) => (cur && cur.attomId === attomId ? fresh : cur));
      setRevalueOutcome({ attomId, valuation, refreshed: !!fresh && fresh.fetchedAt !== before });
    } catch (err) {
      setRevalueOutcome({ attomId, error: (err as Error).message });
    } finally {
      setRevaluingId(null);
    }
  }, [selected]);

  const handleSelectProfile = useCallback(async (attomId: string, opts: { keepFolio?: boolean } = {}) => {
    setIsLoading(true);
    setLoadError(null);
    setZipSheetOpen(false);
    if (!opts.keepFolio) {
      setFolio('brief');
      setBuildingExpanded(false);
      setRollExpanded(false);
    }
    try {
      const profile = await propertyProfileApi.getByAttomId(attomId);
      if (profile) {
        setSelected(profile);
      } else {
        setSelected(null);
        setLoadError('This stored profile was not found.');
      }
    } catch (err) {
      setLoadError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // With a street, the profile whose address1 matches opens directly and
  // keeps the folio the link asked for.
  const runSearch = useCallback(
    async (zip: string, street?: string, opts: { folio?: (typeof FOLIOS)[number] } = {}) => {
      const z = zip.trim();
      if (!z) return;
      setIsSearching(true);
      setSearchError(null);
      setLoadError(null);
      setEmptyZip(null);
      setSelected(null);
      setFolio(opts.folio ?? 'brief');
      try {
        const result = await propertyProfileApi.search(z);
        setResults(result.profiles);
        if (result.profiles.length === 0) {
          setEmptyZip(z);
        } else if (street) {
          const wanted = normalizeStreet(street);
          const match = result.profiles.find((p) => normalizeStreet(p.address1) === wanted);
          if (match) void handleSelectProfile(match.attomId, { keepFolio: true });
        }
      } catch (err) {
        setResults([]);
        setSearchError((err as Error).message);
      } finally {
        setIsSearching(false);
      }
    },
    [handleSelectProfile],
  );

  // Prefill and search from ?zipCode=, ?street= and ?view= once. The Property
  // Valuations "View property" button deep-links with zipCode and street.
  useEffect(() => {
    const zip = searchParams.get('zipCode');
    if (zip) {
      setZipInput(zip);
      const view = searchParams.get('view');
      void runSearch(zip, searchParams.get('street') ?? undefined, { folio: isFolio(view) ? view : 'brief' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The URL names the open profile and folio, so a reload or a shared link
  // reopens it. replace keeps folio changes out of the back stack.
  useEffect(() => {
    if (!selected) return;
    setSearchParams(
      (prev) => {
        const q = new URLSearchParams(prev);
        q.set('zipCode', selected.zipCode);
        q.set('street', selected.address1);
        q.set('view', activeFolio);
        return q;
      },
      { replace: true },
    );
  }, [selected, activeFolio, setSearchParams]);

  // The tablist is in the document flow (admin-portal cannot stick it: the
  // preset sets overflow-x hidden on html and body). A change made from low in
  // a panel brings the tablist to the top of the viewport.
  const changeFolio = useCallback((next: (typeof FOLIOS)[number], opts: { focusTab?: boolean } = {}) => {
    setFolio(next);
    requestAnimationFrame(() => {
      const ids = getTabIds('pp-folio', next);
      const tab = document.getElementById(ids.tabId);
      const panel = document.getElementById(ids.panelId);
      if (panel && panel.getBoundingClientRect().top < 0) {
        tab?.parentElement?.scrollIntoView?.({ block: 'start', behavior: 'instant' });
      }
      if (opts.focusTab) tab?.focus({ preventScroll: true });
    });
  }, []);
  const goFolio = useCallback((next: (typeof FOLIOS)[number]) => changeFolio(next, { focusTab: true }), [changeFolio]);

  const searchProps = {
    zipInput,
    onZipInput: setZipInput,
    onSubmit: () => void runSearch(zipInput),
    isSearching,
    searchError,
    emptyZip,
    results,
    selectedId: selected?.attomId ?? null,
    onSelect: (id: string) => void handleSelectProfile(id),
  };

  const i = selected ? results.findIndex((r) => r.attomId === selected.attomId) : -1;
  const n = results.length;
  const p = selected?.profile;
  const stored = p ? storedCount(p) : 0;
  const jsonData = !selected || jsonKey == null ? null : jsonKey === 'profile' ? selected.profile : selected.profile.sources?.[jsonKey];
  const jsonAt = selected && jsonKey && jsonKey !== 'profile' ? selected.profile.sourcesFetchedAt?.[jsonKey] : undefined;

  const revalueAddress = selected ? valuationAddress(selected) : null;
  const outcome = selected && revalueOutcome?.attomId === selected.attomId ? revalueOutcome : null;

  const railOpen = !selected && !isLoading;
  const stepButton = `inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-sm text-secondary hover:text-primary disabled:text-disabled ${FOCUS}`;

  return (
    <div className="sm:p-6">
      {/* The result rail shows only while no profile is open. An open profile
          takes the full content column at every width; the locator above its
          address changes property. */}
      <div className={railOpen ? 'lg:grid lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-10' : ''}>
        {railOpen && (
          <aside aria-label="Stored profiles">
            <ProfileSearch idPrefix="rail" intro {...searchProps} />
          </aside>
        )}

        <div className={`min-w-0 ${railOpen ? 'mt-8 lg:mt-0' : ''}`}>
          {loadError && (
            <Alert type="error" className="mb-4">
              {loadError}
            </Alert>
          )}
          {isLoading ? (
            <div aria-busy="true" className="space-y-4">
              <VisuallyHidden>Loading profile</VisuallyHidden>
              <Skeleton height="1.75rem" width="70%" />
              <SkeletonText lines={2} />
              <Skeleton height="3rem" width="60%" />
              <Skeleton height="5rem" />
              <SkeletonText lines={4} />
            </div>
          ) : selected && p ? (
            <article id="pp-dossier" aria-labelledby="pp-address" className="min-w-0">
              <nav aria-label="Stored profiles in this ZIP code" className="-mx-2 flex items-center">
                {n > 1 && (
                  <button
                    type="button"
                    aria-label={`Previous property in ZIP ${selected.zipCode}`}
                    disabled={i <= 0}
                    onClick={() => void handleSelectProfile(results[i - 1].attomId, { keepFolio: true })}
                    className={stepButton}
                  >
                    <ChevronLeft aria-hidden="true" className="h-5 w-5" />
                  </button>
                )}
                <button
                  type="button"
                  aria-haspopup="dialog"
                  onClick={() => setZipSheetOpen(true)}
                  className={`inline-flex min-h-11 min-w-0 flex-1 items-center gap-1.5 rounded-sm px-2 text-left font-mono text-caption-md tabular-nums text-tertiary hover:text-primary lg:flex-none ${FOCUS}`}
                >
                  <VisuallyHidden>Change property. </VisuallyHidden>
                  <Joined parts={[`ZIP ${selected.zipCode}`, ...(i >= 0 ? [`${i + 1} of ${n}`] : [])]} />
                  <ChevronDown aria-hidden="true" className="h-4 w-4 shrink-0" />
                </button>
                {n > 1 && (
                  <button
                    type="button"
                    aria-label={`Next property in ZIP ${selected.zipCode}`}
                    disabled={i >= n - 1}
                    onClick={() => void handleSelectProfile(results[i + 1].attomId, { keepFolio: true })}
                    className={stepButton}
                  >
                    <ChevronRight aria-hidden="true" className="h-5 w-5" />
                  </button>
                )}
              </nav>

              <Heading level={2} size="subsection" id="pp-address" className="mt-3">
                {selected.address1}
              </Heading>
              <div className="lg:flex lg:items-center lg:gap-4">
                <p className="mt-1 text-body-sm text-secondary">
                  <Joined parts={[selected.address2, ...(p.apn ? [`APN ${p.apn}`] : [])]} />
                </p>
                {isOperator ? (
                  <button
                    type="button"
                    onClick={() => changeFolio('sources')}
                    className={`${QUIET} mt-1 text-caption-md text-tertiary lg:mt-0`}
                  >
                    {stored > 0
                      ? `${stored} of ${SOURCE_KEYS.length} ATTOM responses, stored ${fmtDateUS(selected.fetchedAt)}`
                      : `Stored ${fmtDateUS(selected.fetchedAt)}, raw ATTOM responses not kept`}
                    <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  <p className="mt-1 text-caption-md text-tertiary lg:mt-0">{`Stored ${fmtDateUS(selected.fetchedAt)}`}</p>
                )}
              </div>

              {isOperator && (
                <PropertyActions
                  address={revalueAddress}
                  busy={revaluingId === selected.attomId}
                  onRevalue={() => setRevalueOpen(true)}
                />
              )}
              {outcome && 'error' in outcome && (
                <Alert type="error" className="mt-2">
                  Revalue failed: {outcome.error}
                </Alert>
              )}
              <p aria-live="polite" className="mt-1 text-caption-md text-secondary">
                {outcome && 'valuation' in outcome && (
                  <RevalueOutcome valuation={outcome.valuation} refreshed={outcome.refreshed} />
                )}
              </p>

              <Tabs
                id="pp-folio"
                aria-label="Profile sections"
                tabs={folios.map((f) => ({ id: f, label: FOLIO_LABELS[f] }))}
                activeTab={activeFolio}
                onTabChange={(id) => changeFolio(id as (typeof FOLIOS)[number])}
                className="mt-3 scroll-mt-4 [&>[role=tab]]:flex-1 [&>[role=tab]]:px-2 lg:[&>[role=tab]]:flex-none lg:[&>[role=tab]]:px-4"
                panelClassName={`pt-6 ${FOCUS}`}
              >
                {activeFolio === 'brief' && (
                  <>
                    <BriefPanel p={p} onFolio={goFolio} showSources={isOperator} />
                    <FolioNext
                      target="property"
                      caption={(() => {
                        const g = propertyGroups(p);
                        const count = g.building.length + g.lot.length + g.systems.length;
                        return count > 0 ? `${count} recorded property fields` : undefined;
                      })()}
                      onGo={() => goFolio('property')}
                    />
                  </>
                )}
                {activeFolio === 'property' && (
                  <PropertyPanel
                    p={p}
                    buildingExpanded={buildingExpanded}
                    onToggleBuilding={() => setBuildingExpanded((x) => !x)}
                    onFolio={goFolio}
                  />
                )}
                {activeFolio === 'history' && (
                  <HistoryPanel
                    p={p}
                    rollExpanded={rollExpanded}
                    onToggleRoll={() => setRollExpanded((x) => !x)}
                    onFolio={goFolio}
                    showSources={isOperator}
                  />
                )}
                {activeFolio === 'sources' && <SourcesPanel p={p} onOpenJson={setJsonKey} onFolio={goFolio} />}
              </Tabs>
            </article>
          ) : results.length > 0 ? (
            <EmptyState
              className="hidden lg:flex"
              title="Choose a stored profile"
              description={`${n} profile${n !== 1 ? 's' : ''} stored in ZIP ${results[0].zipCode}.`}
            />
          ) : !emptyZip && !searchError ? (
            <EmptyState
              title="Search a ZIP code"
              description={
                isOperator
                  ? 'Each stored ATTOM profile opens as a collateral brief: value, record, history and sources.'
                  : 'Each stored ATTOM profile opens as a collateral brief: value, record and history.'
              }
            />
          ) : null}
        </div>
      </div>

      <Sheet
        open={zipSheetOpen}
        onOpenChange={setZipSheetOpen}
        side="right"
        size="xl"
        title="Stored profiles"
        description={n > 0 && selected ? `ZIP ${selected.zipCode}, ${n} profile${n !== 1 ? 's' : ''}` : undefined}
        showCloseButton={false}
        footer={<DoneButton onClose={() => setZipSheetOpen(false)} />}
      >
        <ProfileSearch idPrefix="sheet" {...searchProps} />
      </Sheet>

      <Sheet
        open={jsonKey != null}
        onOpenChange={(o) => {
          if (!o) setJsonKey(null);
        }}
        side="right"
        size="xl"
        title={jsonKey === 'profile' ? 'Full profile JSON' : jsonKey ? SOURCE_LABELS[jsonKey] : ''}
        description={
          jsonKey === 'profile' && selected
            ? `dataSources: ${selected.dataSources.join(', ')}`
            : typeof jsonAt === 'number'
              ? `Fetched ${fmtDateUS(new Date(jsonAt).toISOString())}, ${fmtTime(jsonAt)}`
              : undefined
        }
        showCloseButton={false}
        footer={<DoneButton onClose={() => setJsonKey(null)} />}
      >
        <pre className="overflow-auto rounded-sm bg-elevated p-4 font-mono text-code-sm text-primary">
          {jsonData == null ? '' : JSON.stringify(jsonData, null, 2)}
        </pre>
      </Sheet>

      <Dialog
        open={isOperator && revalueOpen}
        onOpenChange={setRevalueOpen}
        title="Revalue this property"
        showCloseButton={false}
        footer={
          <>
            <button
              type="button"
              onClick={() => setRevalueOpen(false)}
              className={`inline-flex min-h-11 items-center rounded-sm px-3 text-body-sm font-semibold text-secondary hover:text-primary ${FOCUS}`}
            >
              Cancel
            </button>
            <Button variant="primary" onClick={() => void confirmRevalue()}>
              Revalue
            </Button>
          </>
        }
      >
        {revalueAddress && (
          <div className="space-y-3 text-body-sm">
            <p>
              Aaim requests a new ATTOM valuation for {revalueAddress.street}, {revalueAddress.city},{' '}
              {revalueAddress.state} {revalueAddress.zipCode}, and refreshes every stored ATTOM response for it.
            </p>
            <p className="font-semibold text-primary">Cost: about 22 ATTOM calls, billed pay-as-you-go.</p>
            <p>
              A request for this address within 15 minutes of the last one returns that valuation and makes no
              ATTOM call. The result replaces this address&apos;s valuation in Property Valuations.
            </p>
          </div>
        )}
      </Dialog>
    </div>
  );
}
